import { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  ALL_SEVERITIES,
  PAGE_SIZES,
  SOURCE_AGENTS,
  type Severity,
  type SystemLogFilters,
} from './systemLog.types';

// 권위: docs/ARCH_DASHBOARD.md §4.8 필터 바.
// - Severity 체크박스 (FATAL/ERROR/WARN/INFO)
// - Source 다중 선택 (체크박스 드롭다운)
// - Event code glob (자동완성 없이 문자열 입력; 후속 과제)
// - 기간 from/to (datetime-local)
// - q 검색 (summary)
// - 미확인만 토글
// - page_size 드롭다운 (50/100/200)
// - 초기화 버튼

interface Props {
  filters: SystemLogFilters;
  onChange: (next: SystemLogFilters) => void;
  onReset: () => void;
}

export function SystemLogFilterBar({ filters, onChange, onReset }: Props): JSX.Element {
  // q 는 debounce 400ms (타이핑 중 매 keystroke 마다 요청 방지).
  const [qLocal, setQLocal] = useState(filters.q);
  const [codeLocal, setCodeLocal] = useState(filters.eventCode);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setQLocal(filters.q);
  }, [filters.q]);
  useEffect(() => {
    setCodeLocal(filters.eventCode);
  }, [filters.eventCode]);

  const scheduleCommit = useCallback(
    (next: SystemLogFilters) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        onChange(next);
      }, 400);
    },
    [onChange]
  );
  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  const toggleSeverity = useCallback(
    (sev: Severity) => {
      const has = filters.severities.includes(sev);
      const nextSev = has ? filters.severities.filter((s) => s !== sev) : [...filters.severities, sev];
      onChange({ ...filters, severities: nextSev, page: 0 });
    },
    [filters, onChange]
  );

  const toggleSource = useCallback(
    (src: string) => {
      const has = filters.sources.includes(src);
      const nextSrc = has ? filters.sources.filter((s) => s !== src) : [...filters.sources, src];
      onChange({ ...filters, sources: nextSrc, page: 0 });
    },
    [filters, onChange]
  );

  const [sourceOpen, setSourceOpen] = useState(false);
  const sourceRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sourceOpen) return;
    function onDoc(e: MouseEvent): void {
      if (!sourceRef.current) return;
      if (!sourceRef.current.contains(e.target as Node)) setSourceOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [sourceOpen]);

  return (
    <div className="syslog-filter">
      <div className="syslog-filter__group">
        <span className="syslog-filter__label">Severity</span>
        <div className="syslog-chip-row">
          {ALL_SEVERITIES.map((sev) => {
            const active = filters.severities.includes(sev);
            return (
              <button
                key={sev}
                type="button"
                className={`syslog-chip syslog-chip--${sev.toLowerCase()}${
                  active ? ' syslog-chip--active' : ''
                }`}
                onClick={() => toggleSeverity(sev)}
              >
                {sev}
              </button>
            );
          })}
        </div>
      </div>

      <div className="syslog-filter__group" ref={sourceRef}>
        <span className="syslog-filter__label">Source</span>
        <button
          type="button"
          className="syslog-input"
          onClick={() => setSourceOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={sourceOpen}
        >
          {filters.sources.length === 0 ? '전체' : filters.sources.join(', ')}
        </button>
        {sourceOpen && (
          <div className="syslog-filter__dropdown" role="listbox">
            {SOURCE_AGENTS.map((src) => {
              const active = filters.sources.includes(src);
              return (
                <label key={src} className="syslog-filter__dropdown-row">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleSource(src)}
                  />
                  <span>{src}</span>
                </label>
              );
            })}
            {filters.sources.length > 0 && (
              <button
                type="button"
                className="syslog-btn syslog-btn--ghost"
                onClick={() => onChange({ ...filters, sources: [], page: 0 })}
              >
                선택 해제
              </button>
            )}
          </div>
        )}
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">Event code</span>
        <input
          className="syslog-input"
          type="text"
          placeholder="예: LEADING.PIPELINE.*"
          value={codeLocal}
          onChange={(e) => {
            setCodeLocal(e.target.value);
            scheduleCommit({ ...filters, eventCode: e.target.value, page: 0 });
          }}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">From</span>
        <input
          className="syslog-input"
          type="datetime-local"
          value={filters.from}
          onChange={(e) => onChange({ ...filters, from: e.target.value, page: 0 })}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">To</span>
        <input
          className="syslog-input"
          type="datetime-local"
          value={filters.to}
          onChange={(e) => onChange({ ...filters, to: e.target.value, page: 0 })}
        />
      </div>

      <div className="syslog-filter__group syslog-filter__group--grow">
        <span className="syslog-filter__label">검색 (summary)</span>
        <input
          className="syslog-input"
          type="text"
          placeholder="키워드 입력"
          value={qLocal}
          onChange={(e) => {
            setQLocal(e.target.value);
            scheduleCommit({ ...filters, q: e.target.value, page: 0 });
          }}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">&nbsp;</span>
        <label className="syslog-filter__inline">
          <input
            type="checkbox"
            checked={filters.unackedOnly}
            onChange={(e) => onChange({ ...filters, unackedOnly: e.target.checked, page: 0 })}
          />
          <span>미확인만</span>
        </label>
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">Page size</span>
        <select
          className="syslog-input"
          value={filters.pageSize}
          onChange={(e) => onChange({ ...filters, pageSize: parseInt(e.target.value, 10), page: 0 })}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">&nbsp;</span>
        <button type="button" className="syslog-btn syslog-btn--ghost" onClick={onReset}>
          <RotateCcw size={14} strokeWidth={1.75} />
          초기화
        </button>
      </div>
    </div>
  );
}
